import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setStoredToken, setStoredActiveRid } from './api';
import { isPlatformPanelUser } from './platformRoles';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [restaurant, setRestaurant] = useState(null);
    const [impersonating, setImpersonating] = useState(false);
    const [loading, setLoading] = useState(true);
    // Multi-location: list of all restaurants this user owns. For non-owner roles
    // this stays empty; the LocationSwitcher hides itself when there's < 2 items.
    const [myLocations, setMyLocations] = useState([]);

    const loadLocations = useCallback(async (userRole) => {
        if (userRole !== 'restaurant_admin') {
            setMyLocations([]);
            return [];
        }
        try {
            const { data } = await api.get('/r/locations');
            setMyLocations(data.items || []);
            return data.items || [];
        } catch {
            return [];
        }
    }, []);

    const fetchMe = useCallback(async ({ strict = false, silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get('/auth/me');
            setUser(data.user);
            setRestaurant(data.restaurant || null);
            setImpersonating(Boolean(data.impersonating));
            // Refresh the locations list whenever /auth/me re-runs so a fresh
            // login or a refresh after adding/deleting a location stays in sync.
            loadLocations(data.user?.role).catch(() => {});
            return data.user;
        } catch (err) {
            if (err?.response?.status === 401) {
                setUser(null);
                setRestaurant(null);
                setImpersonating(false);
                setMyLocations([]);
                setStoredToken(null);
            }
            if (strict) throw err;
            return null;
        } finally {
            if (!silent) setLoading(false);
        }
    }, [loadLocations]);

    const switchLocation = useCallback(async (restaurantId) => {
        // Persist the choice BEFORE the API call so the request itself already
        // carries the X-Restaurant-Id header (the backend cookie is SameSite=Lax
        // and won't ride along cross-origin to clickmeniu-api.onrender.com).
        setStoredActiveRid(restaurantId);
        await api.put('/r/active-location', { restaurant_id: restaurantId });
        // After switching, teleport the user INTO the new location — land them
        // on the dashboard so they immediately see its data, not the locations
        // list they came from. Using window.location.href (not React Router)
        // triggers a full reload, rebuilding every module-level cache
        // (AuthContext, site settings, live plans, menu locale) against the
        // new tenant in one go.
        window.location.href = '/r';
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async (email, password) => {
        setLoading(true);
        try {
            const { data } = await api.post('/auth/login', { email, password });
            if (data.requires_2fa) {
                setLoading(false);
                return data;
            }
            if (data.access_token) setStoredToken(data.access_token);
            const me = await fetchMe();
            return { ...data, user: me || data.user };
        } catch (err) {
            setLoading(false);
            throw err;
        }
    };

    const verify2fa = async (pendingToken, code) => {
        setLoading(true);
        try {
            const { data } = await api.post('/auth/2fa/verify-login', { pending_token: pendingToken, code });
            if (data.access_token) setStoredToken(data.access_token);
            const me = await fetchMe();
            return { ...data, user: me || data.user };
        } catch (err) {
            setLoading(false);
            throw err;
        }
    };

    const resend2fa = async (pendingToken) => {
        return api.post('/auth/2fa/resend', { pending_token: pendingToken });
    };

    // Legacy single-shot register kept for back-compat with anything that still calls it.
    const registerRestaurant = async (payload) => {
        const { data } = await api.post('/auth/register-restaurant', payload);
        if (data.access_token) setStoredToken(data.access_token);
        await fetchMe();
        return data;
    };

    // New 2-step register: start sends OTP email, verify creates the account.
    const registerStart = async (payload) => {
        const { data } = await api.post('/auth/register/start', payload);
        return data;  // { pending_id, expires_in_seconds, email, dev_code? }
    };

    const registerVerify = async (pendingId, code) => {
        const { data } = await api.post('/auth/register/verify', { pending_id: pendingId, code });
        if (data.access_token) setStoredToken(data.access_token);
        await fetchMe();
        return data;
    };

    const registerResendOtp = async (pendingId) => {
        const { data } = await api.post('/auth/register/resend-otp', { pending_id: pendingId });
        return data;
    };

    const anafLookup = async (cui) => {
        const { data } = await api.get(`/public/anaf/${encodeURIComponent(cui)}`);
        return data;
    };

    const logout = async () => {
        try { await api.post('/auth/logout'); } catch {}
        setStoredToken(null);
        // Drop multi-location preference too — next user on this browser
        // shouldn't inherit the previous owner's last switched-into location.
        setStoredActiveRid(null);
        setUser(null);
        setRestaurant(null);
        setImpersonating(false);
        setLoading(false);
    };

    const enterRestaurant = async (restaurantId, onTokenUpdated) => {
        setLoading(true);
        try {
            // Wipe any owner-side active-location header before impersonating
            // — the impersonation token already encodes which restaurant we're
            // entering, and a stale header could re-route the super-admin into
            // a DIFFERENT tenant on the first /r/* call.
            setStoredActiveRid(null);
            const { data } = await api.post(`/admin/restaurants/${restaurantId}/enter`);
            if (!data.access_token) throw new Error('Token lipsă');
            setStoredToken(data.access_token);
            onTokenUpdated?.();
            const me = await fetchMe({ strict: true });
            if (!isPlatformPanelUser(me?.role) || !me?.restaurant_id) {
                throw new Error('Nu am putut deschide restaurantul');
            }
            return data;
        } catch (err) {
            setLoading(false);
            throw err;
        }
    };

    const exitRestaurant = async (onTokenUpdated) => {
        setLoading(true);
        try {
            const { data } = await api.post('/admin/exit-restaurant');
            if (!data.access_token) throw new Error('Token lipsă');
            setStoredToken(data.access_token);
            setStoredActiveRid(null);
            setRestaurant(null);
            setImpersonating(false);
            setUser((prev) => (prev ? { ...prev, restaurant_id: null, impersonating: false } : prev));
            onTokenUpdated?.();
            const me = await fetchMe({ strict: true });
            if (!isPlatformPanelUser(me?.role) || me?.restaurant_id) {
                throw new Error('Nu am putut reveni la panoul Super Admin');
            }
            return data;
        } catch (err) {
            setLoading(false);
            throw err;
        }
    };

    return (
        <AuthCtx.Provider value={{
            user, restaurant, loading, impersonating,
            login, logout, registerRestaurant,
            registerStart, registerVerify, registerResendOtp, anafLookup,
            refresh: fetchMe,
            enterRestaurant, exitRestaurant, verify2fa, resend2fa,
            myLocations, refreshLocations: () => loadLocations(user?.role), switchLocation,
        }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
